import {
    buildSortingListWritePayload,
    legacyRowsToTableRows,
    normalizeDanishComparable,
    normalizeTransitionColumns,
    positionalRowsToTableRows,
    SORTING_LIST_MODES,
    tableRowsToPositionalList,
} from './wasteReceiverSortingListTransition.js';

const normalizePositionalRows = (rows = [], columnCount = 0) => (
    (Array.isArray(rows) ? rows : []).map((row) => ({
        values: Array.from({ length: columnCount }, (_, index) =>
            String(Array.isArray(row?.values) ? row.values[index] ?? '' : ''),
        ),
    }))
);

export function describeColumnMigration(previousColumns = [], nextColumns = [], nextColumnOrigins = []) {
    const previous = normalizeTransitionColumns(previousColumns);
    const next = normalizeTransitionColumns(nextColumns);
    const origins = next.map((_, nextIndex) => {
        const origin = nextColumnOrigins[nextIndex];
        return Number.isInteger(origin) && origin >= 0 && origin < previous.length
            ? origin
            : null;
    });
    const retainedOrigins = new Set(origins.filter((origin) => origin !== null));

    const addedColumns = next
        .filter((_, index) => origins[index] === null)
        .map((column) => column.display_name);
    const removedColumns = previous
        .filter((_, index) => !retainedOrigins.has(index))
        .map((column) => column.display_name);
    const renamedColumns = next.flatMap((column, nextIndex) => {
        const origin = origins[nextIndex];
        if (origin === null) return [];
        const previousName = previous[origin]?.display_name || '';
        if (previousName === column.display_name) return [];
        return [{ from: previousName, to: column.display_name }];
    });
    const retainedInOriginalOrder = origins.filter((origin) => origin !== null);
    const reordered = retainedInOriginalOrder.some((origin, index) => (
        index > 0 && origin < retainedInOriginalOrder[index - 1]
    ));

    return {
        addedColumns,
        removedColumns,
        renamedColumns,
        reordered,
        nextColumnOrigins: origins,
        previousColumnCount: previous.length,
        nextColumnCount: next.length,
        hasChanges:
            addedColumns.length > 0 ||
            removedColumns.length > 0 ||
            renamedColumns.length > 0 ||
            reordered,
    };
}

export function transformPositionalRowsForColumns(rows = [], migration) {
    const normalizedRows = normalizePositionalRows(rows, migration.previousColumnCount);

    // Keep this index mapping as the single transformation seam if column reordering is added.
    return normalizedRows.map((row) => ({
        values: migration.nextColumnOrigins.map((origin) => (
            origin === null ? '' : row.values[origin] ?? ''
        )),
    }));
}

export function validateCanonicalPositionalRows(rows = [], columnCount = 0) {
    const normalizedRows = normalizePositionalRows(rows, columnCount);
    const blankFirstValueRows = [];
    const rowsByFirstValue = new Map();

    normalizedRows.forEach((row, index) => {
        const comparable = normalizeDanishComparable(String(row.values[0] || '').trim());
        if (!comparable) {
            blankFirstValueRows.push(index + 1);
            return;
        }
        if (!rowsByFirstValue.has(comparable)) rowsByFirstValue.set(comparable, []);
        rowsByFirstValue.get(comparable).push(index + 1);
    });

    return {
        rows: normalizedRows,
        blankFirstValueRows,
        duplicateFirstValueRowGroups: Array.from(rowsByFirstValue.values())
            .filter((rowNumbers) => rowNumbers.length > 1),
    };
}

export function convertLegacySortingItemsToPositionalRows(sortingItems = [], columns = []) {
    // LEGACY_SORTING_ITEMS: Seed the positional representation through the shared legacy adapter.
    const tableRows = legacyRowsToTableRows(sortingItems, columns);
    return tableRowsToPositionalList(tableRows, columns);
}

export function resolveSortingListAddOrUpdateImport({
    existingRows = [],
    importedRows = [],
    mappedColumnIndexes = [],
    columnCount = 0,
}) {
    const existing = normalizePositionalRows(existingRows, columnCount);
    const imported = normalizePositionalRows(importedRows, columnCount);
    const mappedIndexes = Array.from(new Set(mappedColumnIndexes))
        .filter((index) => Number.isInteger(index) && index >= 0 && index < columnCount)
        .sort((left, right) => left - right);
    const blankFirstValueRows = [];
    const importedByKey = new Map();

    imported.forEach((row, index) => {
        const key = normalizeDanishComparable(String(row.values[0] || '').trim());
        if (!key) {
            blankFirstValueRows.push(index + 1);
            return;
        }
        const seen = importedByKey.get(key);
        if (seen) {
            // Last occurrence wins, but keep every source row number so the import preview
            // can show which uploaded rows were folded together.
            seen.row = row;
            seen.sourceIndex = index;
            seen.sourceRowNumbers.push(index + 1);
            return;
        }
        importedByKey.set(key, { row, sourceIndex: index, sourceRowNumbers: [index + 1] });
    });

    const consolidatedImports = Array.from(importedByKey.values())
        .sort((left, right) => left.sourceIndex - right.sourceIndex);
    const duplicatesConsolidated = imported.length - blankFirstValueRows.length - consolidatedImports.length;
    const consolidatedDuplicates = consolidatedImports
        .filter(({ sourceRowNumbers }) => sourceRowNumbers.length > 1)
        .map(({ row, sourceRowNumbers }) => ({
            firstValue: row.values[0],
            sourceRowNumbers: [...sourceRowNumbers],
            usedRowNumber: sourceRowNumbers[sourceRowNumbers.length - 1],
        }))
        .sort((left, right) => left.usedRowNumber - right.usedRowNumber);
    const existingIndexByKey = new Map();
    existing.forEach((row, index) => {
        const key = normalizeDanishComparable(String(row.values[0] || '').trim());
        if (key) existingIndexByKey.set(key, index);
    });

    const rows = existing.map((row) => ({ values: [...row.values] }));
    let updatedCount = 0;
    let addedCount = 0;

    consolidatedImports.forEach(({ row }) => {
        const key = normalizeDanishComparable(String(row.values[0] || '').trim());
        const existingIndex = existingIndexByKey.get(key);
        if (existingIndex === undefined) {
            const values = Array.from({ length: columnCount }, () => '');
            mappedIndexes.forEach((index) => {
                values[index] = row.values[index];
            });
            rows.push({ values });
            existingIndexByKey.set(key, rows.length - 1);
            addedCount += 1;
            return;
        }

        mappedIndexes.forEach((index) => {
            rows[existingIndex].values[index] = row.values[index];
        });
        updatedCount += 1;
    });

    const validation = validateCanonicalPositionalRows(rows, columnCount);
    return {
        ...validation,
        blankImportedFirstValueRows: blankFirstValueRows,
        duplicatesConsolidated,
        consolidatedDuplicates,
        updatedCount,
        addedCount,
        mappedColumnIndexes: mappedIndexes,
    };
}

export function buildDualFormatSortingListImportPayload({
    existingSortingList,
    rows,
    columns,
    version,
    lastUpdate,
}) {
    const tableRows = positionalRowsToTableRows(rows, columns);
    const positionalPayload = buildSortingListWritePayload({
        existingSortingList,
        mode: SORTING_LIST_MODES.POSITIONAL,
        rows: tableRows,
        columns,
        version,
        lastUpdate,
    });

    // LEGACY_SORTING_ITEMS: Imports dual-write through the compatibility helper and its feature flag.
    const legacyPayload = buildSortingListWritePayload({
        existingSortingList,
        mode: SORTING_LIST_MODES.LEGACY,
        rows: tableRows,
        columns,
        version,
        lastUpdate,
    });

    const nextSortingList = { ...positionalPayload.sorting_list };
    if (Object.prototype.hasOwnProperty.call(legacyPayload.sorting_list, 'sorting_items')) {
        nextSortingList.sorting_items = legacyPayload.sorting_list.sorting_items;
    }

    return { sorting_list: nextSortingList };
}
